import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { safeGet, safeRemove } from '../utils/safeStorage';
import { STORAGE_KEYS } from './hooks/storageKeys';
import { useAuthState } from './hooks/useAuthState';
import { useNotificationsState } from './hooks/useNotificationsState';
import { useRiskState } from './hooks/useRiskState';
import { useCountermeasuresState } from './hooks/useCountermeasuresState';
import { useFormResponsesState } from './hooks/useFormResponsesState';
import { useMastersState } from './hooks/useMastersState';

const HGSContext = createContext(null);

export const useHGS = () => {
  const ctx = useContext(HGSContext);
  if (!ctx) throw new Error('useHGS must be used within HGSProvider');
  return ctx;
};

export function HGSProvider({ children }) {
  const auth = useAuthState();
  const { user } = auth;

  const notif = useNotificationsState();
  const risk = useRiskState({ user, addNotification: notif.addNotification });
  const cm = useCountermeasuresState();
  const responses = useFormResponsesState({
    user,
    addCountermeasure: cm.addCountermeasure,
    updateRisk: risk.updateRisk,
    addNotification: notif.addNotification,
  });
  const mst = useMastersState({ user });

  // Static value — not user-mutable in this demo build.
  const [trainingOk] = useState(() => safeGet(STORAGE_KEYS.trainingOk, true));

  // Hard reset for executive walkthroughs — wipes every HGS storage key and
  // returns the user to /login so all hooks re-initialise from defaults.
  const resetDemo = useCallback(() => {
    Object.values(STORAGE_KEYS).forEach((k) => safeRemove(k));
    window.location.assign('/login');
  }, []);

  const value = useMemo(() => ({
    // auth
    user,
    login: auth.login,
    loginAsUser: auth.loginAsUser,
    logout: auth.logout,
    // masters
    masters: mst.masters,
    masterMeta: mst.masterMeta,
    versionHistory: mst.versionHistory,
    versionSnapshots: mst.versionSnapshots,
    pendingApprovals: mst.pendingApprovals,
    setMasterRows: mst.setMasterRows,
    saveMasterUpload: mst.saveMasterUpload,
    submitMasterForApproval: mst.submitMasterForApproval,
    approveMasterSubmission: mst.approveMasterSubmission,
    rejectMasterSubmission: mst.rejectMasterSubmission,
    addApprovalComment: mst.addApprovalComment,
    resetMasters: mst.resetMasters,
    loadDemoMaster: mst.loadDemoMaster,
    // form responses + workflow
    formResponses: responses.formResponses,
    submitFormResponse: responses.submitFormResponse,
    advanceApproval: responses.advanceApproval,
    // risk universe
    riskUniverse: risk.riskUniverse,
    updateRisk: risk.updateRisk,
    confirmAllRisks: risk.confirmAllRisks,
    unconfirmAllRisks: risk.unconfirmAllRisks,
    // New corrected-architecture API
    applicability: risk.applicability,
    evaluations: risk.evaluations,
    setRiskApplicability: risk.setRiskApplicability,
    toggleApplicability: risk.toggleApplicability,
    setEvaluation: risk.setEvaluation,
    getEvaluation: risk.getEvaluation,
    evaluationsForRisk: risk.evaluationsForRisk,
    myAssignedRisks: risk.myAssignedRisks,
    riskPublished: risk.riskPublished,
    publishRiskAssessment: risk.publishRiskAssessment,
    unpublishRiskAssessment: risk.unpublishRiskAssessment,
    fraudTriggered: risk.fraudTriggered,
    triggeringRisks: risk.triggeringRisks,
    pccMandatory: risk.pccMandatory,
    canEditRiskAssessment: user?.role === 'operating_head',
    // countermeasures
    countermeasures: cm.countermeasures,
    addCountermeasure: cm.addCountermeasure,
    closeCountermeasure: cm.closeCountermeasure,
    // notifications
    notifications: notif.notifications,
    addNotification: notif.addNotification,
    markNotificationRead: notif.markNotificationRead,
    markAllNotificationsRead: notif.markAllNotificationsRead,
    // misc
    trainingOk,
    resetDemo,
  }), [user, auth.login, auth.logout, mst, responses, risk, cm, notif, trainingOk, resetDemo]);

  return <HGSContext.Provider value={value}>{children}</HGSContext.Provider>;
}
